import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { useI18n } from '../i18n/useI18n';
import { RotatingHeadline } from './RotatingHeadline';
import { LandingNav } from './LandingNav';
import { LandingFooter } from './LandingFooter';
import CityMarquee from './CityMarquee';
import ScrollReveal from './ScrollReveal';
import LandingSinglists from './LandingSinglists';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  linkLabel: string;
}

function FeatureSection({
  feature,
  reversed,
}: {
  feature: Feature;
  reversed: boolean;
}) {
  return (
    <div className={`landing-feature-section ${reversed ? 'landing-feature-section--reversed' : ''}`}>
      <div className="landing-feature-section__visual">
        <div className="landing-feature-section__icon">{feature.icon}</div>
      </div>
      <div className="landing-feature-section__content">
        <h3 className="landing-feature-section__title">{feature.title}</h3>
        <p className="landing-feature-section__desc">{feature.description}</p>
        <Link to={feature.link} className="landing-feature-section__link">
          {feature.linkLabel}
        </Link>
      </div>
    </div>
  );
}

function LandingPage() {
  const stepsRef = useRef<HTMLElement>(null);
  const { t } = useI18n();

  const FEATURES = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
          <polygon points="10 8 15 11 10 14 10 8" />
        </svg>
      ),
      title: t('landing.features.stageMode.title'),
      description: t('landing.features.stageMode.description'),
      link: '/create-room',
      linkLabel: t('landing.features.learnMore'),
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      ),
      title: t('landing.features.liveQueue.title'),
      description: t('landing.features.liveQueue.description'),
      link: '/create-room',
      linkLabel: t('landing.features.learnMore'),
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      ),
      title: t('landing.features.instantSearch.title'),
      description: t('landing.features.instantSearch.description'),
      link: '/join-room',
      linkLabel: t('landing.features.learnMore'),
    },
  ];

  const STEPS = [
    {
      number: '1',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      ),
      title: t('landing.steps.createRoom.title'),
      description: t('landing.steps.createRoom.description'),
    },
    {
      number: '2',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      ),
      title: t('landing.steps.shareStage.title'),
      description: t('landing.steps.shareStage.description'),
    },
    {
      number: '3',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      ),
      title: t('landing.steps.takeMic.title'),
      description: t('landing.steps.takeMic.description'),
    },
  ];

  useGSAP(
    () => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const numbers = stepsRef.current?.querySelectorAll('.landing-step__number');
      if (!numbers || numbers.length === 0) return;

      if (prefersReducedMotion) {
        gsap.set(numbers, { autoAlpha: 1, y: 0 });
        return;
      }

      gsap.set(numbers, { autoAlpha: 0, y: 50 });

      let observer: IntersectionObserver | null = null;
      const animate = () => {
        gsap.to(numbers, {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.25,
        });
        observer?.disconnect();
        observer = null;
      };

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) animate();
          });
        },
        { threshold: 0.2 }
      );

      if (stepsRef.current) observer.observe(stepsRef.current);

      return () => {
        observer?.disconnect();
      };
    },
    { scope: stepsRef }
  );

  return (
    <div className="landing-page">
      <div className="landing-hero-glow" aria-hidden="true" />

      <div className="landing-content">
        <LandingNav />

        <section className="landing-hero">
          <div className="landing-hero-inner">
            <RotatingHeadline
              base={[
                'Sing',
                'Belt',
                'Wail',
                'Shout',
                'Hymn',
              ]}              
              phrases={[
                  'heart out',
                  'lungs out',
                  'soul out',
                  'voice out',
                  'worries away',
                  'night away',
              ]}
            />
            <p className="landing-hero-lead">
              {t('landing.hero.lead')}
            </p>
            <div className="landing-hero-actions">
              <Link to="/create-room" className="landing-cta">
                {t('common.createRoom')}
              </Link>
              <Link to="/join-room" className="landing-cta landing-cta--secondary">
                {t('common.joinRoom')}
              </Link>
              <Link to="/festival" className="landing-hero-link">
                {t('landing.hero.findEvents')}
              </Link>
            </div>
          </div>
        </section>

        <CityMarquee />

        <section className="landing-section landing-how" ref={stepsRef}>
          <h2 className="landing-section-title">{t('landing.howItWorks.sectionTitle')}</h2>
          <div className="landing-steps">
            {STEPS.map((step) => (
              <div key={step.number} className="landing-step">
                <span className="landing-step__number" aria-hidden="true">
                  {step.number}
                </span>
                <div className="landing-step__icon">{step.icon}</div>
                <h3 className="landing-step__title">{step.title}</h3>
                <p className="landing-step__desc">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        <ScrollReveal className="landing-section landing-singlists-section">
          <LandingSinglists />
        </ScrollReveal>

        <ScrollReveal className="landing-section landing-features">
          <h2 className="landing-section-title">{t('landing.features.sectionTitle')}</h2>
          <div className="landing-features-list">
            {FEATURES.map((f, idx) => (
              <FeatureSection key={idx} feature={f} reversed={idx % 2 === 1} />
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal className="landing-section landing-final-cta">
          <div className="landing-final-cta__content">
            <h2 className="landing-final-cta__title">{t('landing.cta.title')}</h2>
            <p className="landing-final-cta__desc">{t('landing.cta.description')}</p>
            <div className="landing-final-cta__actions">
              <Link to="/create-room" className="landing-cta">
                {t('landing.cta.primary')}
              </Link>
              <Link to="/festival" className="landing-cta landing-cta--secondary">
                {t('landing.cta.secondary')}
              </Link>
            </div>
          </div>
        </ScrollReveal>

        <LandingFooter />
      </div>
    </div>
  );
}

export default LandingPage;
